import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic FormDialog
 *
 * fields: [{ key, label, type?: 'text'|'number'|'textarea'|'select', placeholder?, options?, required?, gridCols? }]
 * - When type is 'select', options must be array of strings
 * - gridCols: 1 (full row) or 2 (half row, pairs)
 */
const FormDialog = ({
  trigger,
  title,
  description,
  fields,
  submitLabel = "Create",
  onSubmit,
  testid,
}) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const setField = (k) => (v) => setForm({ ...form, [k]: v });

  const reset = () => setForm({});

  const submit = async (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && !form[f.key]) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      await onSubmit(form);
      setOpen(false);
      reset();
    } catch (_err) {
      toast.error("Could not create. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Build grid rows: pair up consecutive gridCols:2 fields
  const rows = [];
  let i = 0;
  while (i < fields.length) {
    const f = fields[i];
    if (f.gridCols === 2 && fields[i + 1]?.gridCols === 2) {
      rows.push([f, fields[i + 1]]);
      i += 2;
    } else {
      rows.push([f]);
      i += 1;
    }
  }

  const renderField = (f) => {
    const value = form[f.key] ?? "";
    const common = "mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600";
    return (
      <div key={f.key} className="min-w-0">
        <Label htmlFor={`fd-${f.key}`} className="text-zinc-300">
          {f.label}
          {f.required && <span className="text-blue-400"> *</span>}
        </Label>
        {f.type === "textarea" ? (
          <Textarea
            id={`fd-${f.key}`}
            rows={3}
            placeholder={f.placeholder}
            value={value}
            onChange={(e) => setField(f.key)(e.target.value)}
            className={common}
            data-testid={`${testid}-input-${f.key}`}
          />
        ) : f.type === "select" ? (
          <Select value={value} onValueChange={setField(f.key)}>
            <SelectTrigger
              className={common}
              data-testid={`${testid}-input-${f.key}`}
            >
              <SelectValue placeholder={f.placeholder || "Select"} />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              {(f.options || []).map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={`fd-${f.key}`}
            type={f.type || "text"}
            placeholder={f.placeholder}
            value={value}
            onChange={(e) => setField(f.key)(e.target.value)}
            className={common}
            data-testid={`${testid}-input-${f.key}`}
          />
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-lg"
        data-testid={`${testid}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-50">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-zinc-500">{description}</DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          {rows.map((row, idx) => (
            <div
              key={idx}
              className={
                row.length === 2 ? "grid grid-cols-1 sm:grid-cols-2 gap-4" : ""
              }
            >
              {row.map(renderField)}
            </div>
          ))}
          <DialogFooter className="pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-white/10 bg-transparent text-zinc-200 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-500 text-white"
              data-testid={`${testid}-submit`}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FormDialog;
